import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { trainingManagementService, QuizQuestion } from '../services/trainingManagementService';
import QuizQuestionForm from './QuizQuestionForm';

interface QuizBuilderProps {
  courseId: string;
}

export default function QuizBuilder({ courseId }: QuizBuilderProps) {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const course = await trainingManagementService.getCourse(courseId);
      setQuestions(course.quizQuestions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load quiz questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setShowForm(true);
  };

  const handleEditQuestion = (question: QuizQuestion) => {
    setEditingQuestion(question);
    setShowForm(true);
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await trainingManagementService.deleteQuizQuestion(questionId);
      await loadQuestions();
    } catch (err: any) {
      setError(err.message || 'Failed to delete question');
    }
  };

  const handleSaveQuestion = async (questionData: Partial<QuizQuestion>) => {
    setError(null);
    try {
      if (editingQuestion) {
        await trainingManagementService.updateQuizQuestion(editingQuestion.id, questionData);
      } else {
        await trainingManagementService.addQuizQuestion(courseId, {
          ...questionData,
          courseId,
          orderIndex: questions.length,
        });
      }
      await loadQuestions();
      setShowForm(false);
      setEditingQuestion(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save question');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (showForm) {
    return (
      <QuizQuestionForm
        question={editingQuestion}
        onSave={handleSaveQuestion}
        onCancel={() => {
          setShowForm(false);
          setEditingQuestion(null);
        }}
      />
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {questions.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No quiz questions yet
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddQuestion}
            sx={{ mt: 2 }}
          >
            Add First Question
          </Button>
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary">
              {questions.length} question{questions.length !== 1 ? 's' : ''}
            </Typography>
            <Button startIcon={<Add />} size="small" onClick={handleAddQuestion}>
              Add Question
            </Button>
          </Box>

          {/* Question List */}
          {questions.map((question, index) => (
            <Box
              key={question.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography variant="body2" color="text.secondary">
                    Question {index + 1}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {question.question}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Type: {question.questionType} | Points: {question.points}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button size="small" onClick={() => handleEditQuestion(question)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDeleteQuestion(question.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}

